import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  BookOpen, 
  Search, 
  RefreshCw,
  Calendar,
  Shield,
  FileCheck,
  AlertTriangle,
  Clock,
  ChevronRight
} from 'lucide-react';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';
import PHSCommandLayout from '@/components/phs/enterprise/PHSCommandLayout';

const API = process.env.REACT_APP_BACKEND_URL;

// Frequency colors
const FREQUENCY_COLORS = {
  'Annual': 'bg-blue-100 text-blue-700',
  'Bi-Annual': 'bg-purple-100 text-purple-700',
  'Quarterly': 'bg-amber-100 text-amber-700',
  'Monthly': 'bg-green-100 text-green-700',
  '5 Yearly': 'bg-red-100 text-red-700',
  '3 Yearly': 'bg-orange-100 text-orange-700'
};

function PPMLibraryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    loadPPMLibrary();
  }, []);
  
  const loadPPMLibrary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/phs/ppm-library`, { headers });
      setItems(res.data.items || []);
      setCategories(res.data.by_category || {});
    } catch (err) {
      console.error('Failed to load PPM library:', err);
      toast.error('Failed to load PPM library');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = !searchTerm || 
      item.task_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const categoryList = Object.keys(categories);
  
  return (
    <PHSCommandLayout>
    <div className="min-h-screen bg-slate-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-teal-600" />
              PPM Library
            </h1>
            <p className="text-sm text-slate-500 mt-1">Planned preventative maintenance task library</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadPPMLibrary}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search PPM tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="ppm-search"
          />
        </div>
      </div>
      
      {/* Category Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            selectedCategory === 'all'
              ? 'bg-teal-600 text-white'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          All ({items.length})
        </button>
        {categoryList.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat === selectedCategory ? 'all' : cat)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              selectedCategory === cat
                ? 'bg-teal-600 text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {cat} ({categories[cat]?.length || 0})
          </button>
        ))}
      </div>
      
      {/* PPM Items Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-4" />
                <div className="h-4 bg-slate-200 rounded w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <Card className="p-8 text-center">
          <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No PPM Tasks Found</h3>
          <p className="text-sm text-slate-500">
            {searchTerm ? 'Try adjusting your search' : 'No PPM tasks have been configured'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item, idx) => {
            const freqColor = FREQUENCY_COLORS[item.frequency] || 'bg-slate-100 text-slate-700';
            
            return (
              <Card 
                key={item.id || idx} 
                className="hover:shadow-lg transition-shadow"
                data-testid={`ppm-item-${idx}`}
              >
                <CardContent className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <Badge className="text-[10px] mb-1 bg-slate-100 text-slate-600">
                        {item.category}
                      </Badge>
                      <h3 className="font-semibold text-slate-800">{item.task_name}</h3>
                    </div>
                    {item.is_legal_requirement && (
                      <Shield className="h-5 w-5 text-red-500 flex-shrink-0" />
                    )}
                  </div>
                  
                  {/* Description */}
                  {item.description && (
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                      {item.description}
                    </p>
                  )}
                  
                  {/* Metadata */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={`text-[10px] ${freqColor}`}>
                      <Calendar className="h-3 w-3 mr-1" />
                      {item.frequency}
                    </Badge>
                    
                    {item.typical_cost && (
                      <Badge variant="outline" className="text-[10px]">
                        £{item.typical_cost}
                      </Badge>
                    )}
                    
                    {item.certificate_required && (
                      <Badge variant="outline" className="text-[10px] text-green-600 border-green-200">
                        <FileCheck className="h-3 w-3 mr-1" />
                        Certificate
                      </Badge>
                    )}
                  </div>
                  
                  {/* Trade */}
                  {item.contractor_trade && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <span className="text-xs text-slate-400">Trade: </span>
                      <span className="text-xs text-slate-600 font-medium">{item.contractor_trade}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </PHSCommandLayout>
  );
}

export default withWyldecrestAuth(PPMLibraryPage, 'helpdesk');
